import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// 8 columns × 4 rows — visually distinct hues, then neutrals
const SWATCHES = [
  // Reds → Oranges → Yellows
  '#ef4444', '#dc2626', '#f97316', '#ea580c', '#eab308', '#ca8a04', '#facc15', '#fbbf24',
  // Greens
  '#22c55e', '#16a34a', '#14b8a6', '#0d9488', '#10b981', '#059669', '#84cc16', '#65a30d',
  // Blues → Indigo → Violet
  '#3b82f6', '#2563eb', '#6366f1', '#4f46e5', '#8b5cf6', '#7c3aed', '#a855f7', '#9333ea',
  // Pinks → Neutrals
  '#ec4899', '#db2777', '#f43f5e', '#e11d48', '#000000', '#374151', '#9ca3af', '#ffffff',
];

const ColorPicker = ({ anchorRef, value, onSelect, onClear, onClose }) => {
  const [draft, setDraft] = useState(value || '');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const pickerRef = useRef(null);
  const PANEL_W = 252;

  useEffect(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const left = Math.min(r.left, window.innerWidth - PANEL_W - 8);
    setPos({ top: r.bottom + 6, left: Math.max(8, left) });
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  const commit = (hex) => { onSelect(hex); onClose(); };

  return createPortal(
    <div
      ref={pickerRef}
      className="rounded-xl shadow-2xl bg-white"
      style={{
        position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
        width: PANEL_W, padding: 14,
        border: '1px solid rgba(99,102,241,0.15)',
      }}
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Swatch grid — 8 columns */}
      <div
        className="mb-3"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6 }}
      >
        {SWATCHES.map(c => {
          const isWhite = c === '#ffffff';
          const isSelected = draft === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => commit(c)}
              title={c}
              style={{
                width: 22, height: 22,
                borderRadius: 6,
                background: c,
                border: isWhite ? '1px solid #d1d5db' : isSelected ? `2px solid ${c}` : '2px solid transparent',
                outline: isSelected ? `2px solid ${c}` : 'none',
                outlineOffset: 2,
                transition: 'transform 120ms',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
          );
        })}
      </div>

      {/* Native color picker + hex input + clear */}
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={draft || '#000000'}
          onChange={e => setDraft(e.target.value)}
          onBlur={e => commit(e.target.value)}
          title="Custom color"
          style={{
            width: 32, height: 32, flexShrink: 0,
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 8, cursor: 'pointer', padding: 2,
          }}
        />
        <input
          type="text"
          value={draft}
          maxLength={7}
          placeholder="#000000"
          onChange={e => {
            const v = e.target.value;
            setDraft(v);
            if (/^#[0-9a-fA-F]{6}$/.test(v)) commit(v);
          }}
          style={{
            flex: 1, height: 32, padding: '0 8px',
            fontSize: 12, fontFamily: 'monospace',
            border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8,
            outline: 'none', color: 'rgba(15,15,25,0.75)',
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(99,102,241,0.2)'}
        />
        {value && (
          <button
            type="button"
            onClick={() => { onClear(); onClose(); }}
            title="Clear color"
            style={{
              flexShrink: 0, fontSize: 11, padding: '2px 6px',
              borderRadius: 6, color: '#dc2626',
              background: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.15)',
              cursor: 'pointer',
            }}
          >✕</button>
        )}
      </div>
    </div>,
    document.body,
  );
};

const ColorCustomField = ({ field, updateField }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const saved = field?.value || '';

  return (
    <div className="flex items-center w-full px-3" style={{ minHeight: 40 }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center gap-2.5 hover:opacity-75 transition-opacity"
      >
        {saved ? (
          <>
            <span
              style={{
                display: 'inline-block', width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                background: saved, border: '1px solid rgba(0,0,0,0.12)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              }}
            />
            <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'rgba(15,15,25,0.7)' }}>
              {saved}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 13, color: 'rgba(15,15,25,0.28)' }}>Pick a color…</span>
        )}
      </button>

      {open && (
        <ColorPicker
          anchorRef={btnRef}
          value={saved}
          onSelect={hex => updateField(field._id, hex)}
          onClear={() => updateField(field._id, null)}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export default ColorCustomField;
